import React from 'react';
import { levelConfig } from '../../data/portfolioData';
import { Lock, CheckCircle2, Play } from 'lucide-react';

export const LevelSelector = ({ 
  currentLevel, 
  completedLevels, 
  onSelectLevel,
  onClose 
}) => {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="level-selector">
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 rounded-none border-4 border-black max-w-2xl w-full p-6 max-h-[90vh] overflow-auto retro-ui">
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-[#f8d434] drop-shadow-lg">
            SELECT WORLD
          </h2>
          <p className="text-white/80 mt-1">Explore Mushroom Kingdom levels</p>
        </div>
        
        <div className="grid gap-4">
          {levelConfig.map((level, index) => {
            const isCompleted = completedLevels.includes(level.id);
            const isLocked = index > 0 && !completedLevels.includes(levelConfig[index - 1].id);
            const isCurrent = currentLevel === level.id;
            
            return (
              <button
                key={level.id}
                onClick={() => !isLocked && onSelectLevel(level.id)}
                disabled={isLocked}
                className={`
                  relative p-4 rounded-2xl border-2 text-left transition-all
                  ${isLocked 
                    ? 'bg-slate-100 border-slate-200 cursor-not-allowed opacity-60' 
                    : isCurrent
                      ? 'bg-gradient-to-r from-sky-50 to-purple-50 border-purple-300 shadow-lg'
                      : 'bg-white border-slate-200 hover:border-purple-300 hover:shadow-md'
                  }
                `}
                data-testid={`level-${level.id}-btn`}
              >
                <div className="flex items-center gap-4">
                  {/* Level Number */}
                  <div 
                    className={`
                      w-14 h-14 rounded-xl flex items-center justify-center font-bold text-xl
                      ${isLocked 
                        ? 'bg-slate-200 text-slate-400' 
                        : isCompleted
                          ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 text-white'
                          : 'bg-gradient-to-br from-sky-400 to-purple-500 text-white'
                      }
                    `}
                    style={!isLocked && !isCompleted ? { background: level.gradient } : {}}
                  >
                    {isLocked ? <Lock className="w-6 h-6" /> : level.id}
                  </div>
                  
                  {/* Level Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-bold text-lg ${isLocked ? 'text-slate-400' : 'text-slate-800'}`}>
                        {level.name}
                      </h3>
                      {isCompleted && (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      )}
                    </div>
                    <p className={`text-sm ${isLocked ? 'text-slate-400' : 'text-slate-500'}`}>
                      {level.description}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${isLocked ? 'bg-slate-200 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>
                        {level.items.length} items
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${isLocked ? 'bg-slate-200 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>
                        {level.npcFacts.length} NPCs
                      </span>
                    </div>
                  </div>
                  
                  {/* Play button */}
                  {!isLocked && (
                    <div className={`
                      w-12 h-12 rounded-full flex items-center justify-center
                      ${isCurrent 
                        ? 'bg-purple-500 text-white' 
                        : 'bg-slate-100 text-slate-600 group-hover:bg-purple-100'
                      }
                    `}>
                      <Play className="w-5 h-5" />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        
        <button
          onClick={onClose}
          className="mt-6 w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-colors"
          data-testid="close-level-selector-btn"
        >
          Back to Game
        </button>
      </div>
    </div>
  );
};

export default LevelSelector;
